import React, { useState, useEffect } from 'react';
import '../styles/Landing.css';
import { useAuth } from '../components/AuthContext';
const API_BASE = import.meta.env.VITE_API_BASE;

/* ── SVG icon components ── */
const IconChart = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="#6366f1" strokeWidth="1.5" strokeLinecap="round">
    <rect x="2" y="10" width="3" height="8" rx="1" /><rect x="8.5" y="6" width="3" height="12" rx="1" />
    <rect x="15" y="2" width="3" height="16" rx="1" />
  </svg>
);

const IconConfig = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="#0f766e" strokeWidth="1.5" strokeLinecap="round">
    <rect x="3" y="3" width="14" height="14" rx="2" />
    <polyline points="7,7 9,9 7,11" /><line x1="10" y1="10" x2="14" y2="10" />
    <line x1="7" y1="14" x2="13" y2="14" />
  </svg>
);

const IconGear = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="#b45309" strokeWidth="1.5" strokeLinecap="round">
    <circle cx="10" cy="10" r="3" />
    <path d="M10 2v2m0 12v2m-5.66-3.34 1.42-1.42m8.48-8.48 1.42-1.42M2 10h2m12 0h2m-3.34 5.66-1.42-1.42M5.76 5.76 4.34 4.34" />
  </svg>
);

const IconTable = () => (
  <svg viewBox="0 0 20 20" fill="none" stroke="#475569" strokeWidth="1.5" strokeLinecap="round">
    <rect x="2" y="2" width="16" height="16" rx="2" />
    <line x1="2" y1="7" x2="18" y2="7" /><line x1="2" y1="12" x2="18" y2="12" />
    <line x1="7" y1="7" x2="7" y2="18" /><line x1="13" y1="7" x2="13" y2="18" />
  </svg>
);

/* ── Decorative tile SVGs ── */
const DecoChart = () => (
  <svg className="tile-decoration" viewBox="0 0 120 120">
    <rect x="10" y="50" width="18" height="60" rx="4" fill="#6366f1" />
    <rect x="38" y="30" width="18" height="80" rx="4" fill="#6366f1" />
    <rect x="66" y="10" width="18" height="100" rx="4" fill="#6366f1" />
    <rect x="94" y="40" width="18" height="70" rx="4" fill="#6366f1" />
  </svg>
);

const DecoConfig = () => (
  <svg className="tile-decoration" viewBox="0 0 120 120">
    <rect x="10" y="15" width="100" height="14" rx="4" fill="#0f766e" />
    <rect x="10" y="38" width="80" height="14" rx="4" fill="#0f766e" />
    <rect x="10" y="61" width="60" height="14" rx="4" fill="#0f766e" />
    <rect x="10" y="84" width="90" height="14" rx="4" fill="#0f766e" />
  </svg>
);

const DecoGear = () => (
  <svg className="tile-decoration" viewBox="0 0 120 120">
    <circle cx="60" cy="60" r="30" stroke="#b45309" strokeWidth="6" fill="none" />
    <circle cx="60" cy="60" r="12" fill="#b45309" />
    <circle cx="60" cy="20" r="5" fill="#b45309" />
    <circle cx="94" cy="40" r="5" fill="#b45309" />
    <circle cx="94" cy="80" r="5" fill="#b45309" />
    <circle cx="60" cy="100" r="5" fill="#b45309" />
    <circle cx="26" cy="80" r="5" fill="#b45309" />
    <circle cx="26" cy="40" r="5" fill="#b45309" />
  </svg>
);

const DecoTable = () => (
  <svg className="tile-decoration" viewBox="0 0 120 120">
    <rect x="8" y="8" width="104" height="104" rx="8" stroke="#475569" strokeWidth="4" fill="none" />
    <line x1="8" y1="36" x2="112" y2="36" stroke="#475569" strokeWidth="3" />
    <line x1="8" y1="64" x2="112" y2="64" stroke="#475569" strokeWidth="3" />
    <line x1="8" y1="92" x2="112" y2="92" stroke="#475569" strokeWidth="3" />
    <line x1="44" y1="36" x2="44" y2="112" stroke="#475569" strokeWidth="3" />
    <line x1="78" y1="36" x2="78" y2="112" stroke="#475569" strokeWidth="3" />
  </svg>
);

function Landing({ onNavigate }) {
  const { user, workspace, apiFetch } = useAuth();
  const [activity, setActivity] = useState({
    totalConfigs: 0,
    totalGenerations: 0,
    lastConfig: null,
    lastGeneration: null,
    savedConfigs: []
  });
  const [configs, setConfigs] = useState([]);
  const [outputFolders, setOutputFolders] = useState([]);

  useEffect(() => {
    loadActivitySummary();
    loadSavedConfigs();
    loadOutputStats();
  }, [workspace?.id]);

  const loadActivitySummary = () => {
    const summary = JSON.parse(localStorage.getItem('activity_summary') || '{}');
    setActivity({
      totalConfigs: 0,
      totalGenerations: summary.totalGenerations || 0,
      lastConfig: summary.lastConfig || null,
      lastGeneration: summary.lastGeneration || null,
      savedConfigs: []
    });
  };

  const loadSavedConfigs = async () => {
    try {
      const resp = await apiFetch(`${API_BASE}/configs`);
      if (resp.ok) {
        const data = await resp.json();
        setConfigs(data.configs || []);
      }
    } catch (err) {
      console.log('Could not load configs');
    }
  };

  const loadOutputStats = async () => {
    try {
      const resp = await apiFetch(`${API_BASE}/outputs/browse`);
      if (resp.ok) {
        const data = await resp.json();
        setOutputFolders(data.folders || []);
      }
    } catch (err) {
      console.log('Could not load output stats');
    }
  };

  const totalRows = outputFolders.reduce((sum, f) => sum + (f.file_count || 0) * 500, 0);
  const totalCsvs = outputFolders.reduce((sum, f) => sum + (f.file_count || 0), 0);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000) return 'Today';
    if (diff < 172800000) return 'Yesterday';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="landing-page">
      {/* Hero */}
      <div className="landing-hero">
        <span className="hero-label">WORKSPACE</span>
        <h1>{getGreeting()}, {(user?.display_name || user?.name)?.split(' ')[0] || 'there'}.</h1>
        <p>Here's everything at a glance — pick a section to continue.</p>
      </div>

      <div className="tile-grid">
        {/* ── Dashboard (wide) ── */}
        <div className="tile wide" onClick={() => onNavigate('history')}>
          <DecoChart />
          <div className="tile-inner">
            <div>
              <div className="tile-icon-wrap icon-indigo"><IconChart /></div>
              <h2>Dashboard</h2>
              <p className="tile-desc">
                A live overview of your workspace — configs, generation runs, datasets, and recent activity all in one place.
              </p>

              <div className="stat-row">
                <div className="stat-block">
                  <span className="stat-n">{configs.length}</span>
                  <span className="stat-l">CONFIGS</span>
                </div>
                <div className="stat-block">
                  <span className="stat-n">{outputFolders.length}</span>
                  <span className="stat-l">DATASETS</span>
                </div>
                <div className="stat-block">
                  <span className="stat-n">{totalRows.toLocaleString()}</span>
                  <span className="stat-l">ROWS</span>
                </div>
                <div className="stat-block">
                  <span className="stat-n">{totalCsvs}</span>
                  <span className="stat-l">CSVS</span>
                </div>
              </div>

              <div className="chip-row">
                <span className="chip chip-green">Workspace active</span>
              </div>
            </div>

            <div>
              <h4 className="mini-heading">RECENT ACTIVITY</h4>
              <table className="mini-table">
                <thead>
                  <tr><th>DATASET</th><th>ROWS</th><th>DATE</th></tr>
                </thead>
                <tbody>
                  {outputFolders.slice(0, 3).map((f, i) => (
                    <tr key={i}>
                      <td><span className={`dot ${i === 0 ? 'dot-green' : ''}`}></span>{f.folder}</td>
                      <td>{f.file_count * 500}</td>
                      <td>{formatDate(f.timestamp)}</td>
                    </tr>
                  ))}
                  {outputFolders.length === 0 && (
                    <tr><td colSpan="3" className="muted">No datasets yet</td></tr>
                  )}
                </tbody>
              </table>
              <div className="tile-footer" onClick={() => onNavigate('history')}>Open history →</div>
            </div>
          </div>
        </div>

        {/* ── Design Studio ── */}
        <div className="tile" onClick={() => onNavigate('config')}>
          <DecoConfig />
          <div className="tile-icon-wrap icon-teal"><IconConfig /></div>
          <h2>Design Studio</h2>
          <p className="tile-desc">
            Define your data model — name fields, set types, configure constraints. Save designs and reload them anytime.
          </p>

          <h4 className="mini-heading">SAVED MODELS</h4>
          <div className="config-list-mini">
            {configs.slice(0, 3).map((c, i) => (
              <div key={i} className="config-row-mini">
                <span>{c.name}</span>
                <span className="muted">{c.entities || 0} entities</span>
              </div>
            ))}
            {configs.length === 0 && (
              <div className="config-row-mini muted">No configs yet</div>
            )}
          </div>

          <div className="chip-row mt-auto">
            <span className="chip chip-teal">{configs.length} configs</span>
            <span className="chip chip-outline" onClick={(e) => { e.stopPropagation(); onNavigate('config'); }}>+ New</span>
          </div>
          <div className="tile-footer">Open studio →</div>
        </div>

        {/* ── Data Generator ── */}
        <div className="tile" onClick={() => onNavigate('pipeline')}>
          <DecoGear />
          <div className="tile-icon-wrap icon-amber"><IconGear /></div>
          <h2>Data Generator</h2>
          <p className="tile-desc">
            Load a config, validate the schema, set row count, generate — then validate output before saving to your datasets.
          </p>

          <h4 className="mini-heading">PIPELINE STEPS</h4>
          <div className="progress-strip">
            <div className="progress-track">
              <div className="progress-fill" style={{ width: '100%' }}></div>
            </div>
            <div className="progress-labels">
              <span>Select</span>
              <span>Validate</span>
              <span>Generate</span>
              <span>Confirm</span>
            </div>
          </div>

          <p className="card-meta">
            Last run: <strong>{outputFolders.length > 0
              ? new Date(outputFolders[0].created_at || outputFolders[0].timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
              : 'Never'}</strong>
          </p>

          <div className="chip-row mt-auto">
            <span className="chip chip-amber">{outputFolders.length} runs</span>
            {outputFolders.length > 0 && (
              <span className="chip chip-indigo">{outputFolders.reduce((sum, f) => sum + (f.file_count || 0), 0)} files generated</span>
            )}
          </div>
          <div className="tile-footer">Open pipeline →</div>
        </div>

        {/* ── Data Viewer (wide) ── */}
        <div className="tile wide" onClick={() => onNavigate('outputs')}>
          <DecoTable />
          <div className="tile-inner">
            <div>
              <div className="tile-icon-wrap icon-slate"><IconTable /></div>
              <h2>Data Viewer</h2>
              <p className="tile-desc">
                Browse, search, and inspect all generated datasets. Filter by config, paginate through rows, and export any dataset as CSV.
              </p>

              <div className="chip-row">
                <span className="chip chip-indigo">{outputFolders.length} datasets</span>
                <span className="chip chip-indigo">{totalRows.toLocaleString()} rows</span>
                <span className="chip chip-slate">{totalCsvs} CSV exports</span>
              </div>

              <div className="mini-bars">
                {[60,85,45,70,55,90,40,75,50,80,65,95,35].map((h, i) => (
                  <span key={i} style={{ height: `${h}%` }} />
                ))}
              </div>
            </div>

            <div>
              <table className="mini-table">
                <thead>
                  <tr><th>#</th><th>FULL_NAME</th><th>EMAIL</th><th>AGE</th><th>STATUS</th></tr>
                </thead>
                <tbody>
                  <tr><td>1</td><td>Alice Johnson</td><td>user4821@…</td><td>34</td><td><span className="chip chip-sm chip-green">true</span></td></tr>
                  <tr><td>2</td><td>Marco Davis</td><td>user9043@…</td><td>28</td><td><span className="chip chip-sm chip-red">false</span></td></tr>
                  <tr><td>3</td><td>Nina Williams</td><td>user2267@…</td><td>51</td><td><span className="chip chip-sm chip-green">true</span></td></tr>
                </tbody>
              </table>
              <div className="tile-footer">Open viewer →</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Landing;
